import { LESSON_STARTING_HEARTS } from '@/components/v2/lesson/lessonVocabulary'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'

function HeartIcon({ className }: { className: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M12 21s-7.5-4.7-9.6-9A5.6 5.6 0 0 1 12 6.2 5.6 5.6 0 0 1 21.6 12c-2.1 4.3-9.6 9-9.6 9Z" />
    </svg>
  )
}

/**
 * The lesson's own chrome, which replaces the funnel chrome the way Duolingo's
 * does: a quit cross, progress scoped to the exercises in this lesson, and the
 * remaining hearts.
 *
 * Both readouts deliberately part ways with Duolingo. Progress is a segment
 * per exercise rather than one continuous bar, because the lesson is four or
 * five exercises long: at that length a sliver of a bar says far less than
 * "one of four done", and an exercise re-queued after a miss visibly adds a
 * segment. Hearts are pips rather than a heart and a number, so the cost of a
 * wrong answer is legible at a glance instead of being a digit that ticks
 * down.
 */
export default function DuoLessonHeader({
  completed,
  hearts,
  onQuit,
  total,
}: {
  /** Exercises answered so far. */
  completed: number
  hearts: number
  onQuit: () => void
  total: number
}) {
  const { t } = useTranslation()

  return (
    <div className="flex shrink-0 items-center gap-3">
      <button
        aria-label={t('pages.onboarding.v2.lesson.quit')}
        className="text-duo-line-dark hover:text-duo-ink flex h-9 w-9 shrink-0 items-center justify-center transition-colors"
        data-testid="button-quit-lesson"
        onClick={onQuit}
        type="button"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
      </button>

      <div
        aria-valuemax={total}
        aria-valuemin={0}
        aria-valuenow={completed}
        className="flex grow items-center gap-1.5"
        role="progressbar"
      >
        {Array.from({ length: total }, (_, index) => (
          <span
            className={twMerge(
              'relative h-3.5 grow overflow-hidden rounded-full transition-colors duration-300 ease-out',
              index < completed ? 'bg-duo-green' : 'bg-duo-line',
            )}
            key={index}
          >
            {/* Duolingo's gloss, which is what stops a filled pill reading as
                a flat block of colour. */}
            {index < completed && (
              <span className="absolute inset-x-1.5 top-1 h-1 rounded-full bg-white/45" />
            )}
          </span>
        ))}
      </div>

      <div
        className="flex shrink-0 items-center gap-0.5"
        data-hearts={hearts}
        data-testid="lesson-hearts"
      >
        {Array.from({ length: LESSON_STARTING_HEARTS }, (_, index) => (
          <HeartIcon
            className={twMerge(
              // `scale` and `rotate` rather than `transform`: Tailwind v4
              // emits those utilities as standalone properties, so a
              // transition on `transform` leaves the pip snapping.
              'h-5 w-5 transition-[color,scale,rotate] duration-300 ease-out',
              index < hearts
                ? 'text-duo-red'
                : 'text-duo-line scale-75 rotate-12',
            )}
            key={index}
          />
        ))}

        <span className="sr-only">
          {t('pages.onboarding.v2.lesson.hearts')}: {hearts}
        </span>
      </div>
    </div>
  )
}
